import { useState } from "react"
import Icon from "@/v2-ui/icon"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import LegalBillingLineTable from "@/v2-backoffice-client/legalBilling/LegalBillingLineTable"
import LegalBillingTotalsSummary from "@/v2-backoffice-client/legalBilling/LegalBillingTotalsSummary"
import LegalBillingInvoiceActions from "@/v2-backoffice-client/legalBilling/LegalBillingInvoiceActions"
import type {
  LegalBillingBatch,
  LegalBillingInvoiceAction,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingBatchRowProps = {
  batch: LegalBillingBatch
  isPending?: boolean
  onInvoice(action: LegalBillingInvoiceAction): void
}

// One checkout batch: collapsed to its totals by default, expandable to the document
// lines. Collapsed by default because the dashboard's job is deciding what to bill, which
// is a totals question; the lines matter only once an admin is reconciling a specific
// invoice.
function LegalBillingBatchRow(props: LegalBillingBatchRowProps) {
  const { batch, isPending, onInvoice } = props
  const [isOpen, setIsOpen] = useState(false)

  const placedBy =
    batch.CreatedByName ||
    batch.CreatedByEmail ||
    `User ${batch.CreatedByUserId}`

  return (
    <div className="legal-billing-batch border-t border-border py-2 pl-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="flex items-center gap-2 text-sm"
          aria-expanded={isOpen}
          aria-label={`Batch ${batch.OrderId} by ${placedBy}`}
          data-feature-code="UI_BackofficeLegalBilling_Batch_Toggle"
          onClick={() => setIsOpen((open) => !open)}
        >
          <Icon src={isOpen ? "angle-small-down" : "angle-small-right"} />
          <span className="font-medium">Batch {batch.OrderId}</span>
          <span className="text-muted-foreground">{placedBy}</span>
          <span className="text-muted-foreground">
            <RelativeDate date={batch.CreatedAt} />
          </span>
        </button>

        <div className="flex items-center gap-3">
          <LegalBillingTotalsSummary
            totals={batch.Totals}
            invoiced={batch.Invoiced}
          />
          <LegalBillingInvoiceActions
            batches={[batch]}
            isPending={isPending}
            onInvoice={onInvoice}
            scopeLabel={`batch ${batch.OrderId}`}
          />
        </div>
      </div>

      {isOpen ? (
        <div className="pl-6 pt-2">
          <LegalBillingLineTable lines={batch.Lines} />
        </div>
      ) : null}
    </div>
  )
}

export default LegalBillingBatchRow
